/**
 * useCompanyNavigationStack - Shared hook for stack-based company overlay navigation
 *
 * Used by parent views (ProfilerProjectView, ProfilerFeedbackRequestView, ProfilerWatchList)
 * to manage company details overlay navigation with back button support and state preservation.
 *
 * @module useCompanyNavigationStack
 */

import { useLocalObservable } from 'mobx-react';
import { runInAction, observable } from 'mobx';

// Module-level scroll state (persists across component mounts)
const scrollState = observable({
  keyed: {}, // { [projectId]: scrollY } for Project/Feedback views
  scalar: 0 // Single value for WatchList
});

/**
 * Hook for managing stack-based company navigation in overlay mode
 *
 * @param {Object} options - Configuration options
 * @param {string|null} options.scrollKey - Key for scroll position storage.
 *        - Pass project.id for keyed storage (multiple projects can have independent scroll positions)
 *        - Pass null for scalar storage (single scroll position, used by WatchList)
 *
 * @returns {Object} MobX observable with navigation state and actions:
 *   - companiesStack: Array of { company, savedTab, savedComparablesId }
 *   - isBackNavigation: Boolean flag to signal back navigation to CompanyDetails
 *   - detailsDisplayed: Computed - top company from stack or null
 *   - savedTabForCurrentCompany: Computed - savedTab from top stack entry
 *   - savedComparablesIdForCurrentCompany: Computed - savedComparablesId from top stack entry
 *   - displayDetails(entry, currentTabOrUnmount, currentComparablesId): Push/pop/clear stack
 *   - goBackToPreviousCompany(): Pop stack and set isBackNavigation flag
 *   - clearBackNavigationFlag(): Reset isBackNavigation to false
 */
export const useCompanyNavigationStack = ({ scrollKey = null } = {}) => {
  // Helper to restore scroll position and clear saved state
  const restoreScrollAndClear = () => {
    const savedScrollY = scrollKey ? scrollState.keyed[scrollKey] || 0 : scrollState.scalar;
    window.scrollTo({ top: savedScrollY });
    runInAction(() => {
      if (scrollKey) {
        delete scrollState.keyed[scrollKey];
      } else {
        scrollState.scalar = 0;
      }
    });
  };

  // Helper to clear scroll state without scrolling (for unmount cleanup)
  const clearScrollState = () => {
    runInAction(() => {
      if (scrollKey) {
        delete scrollState.keyed[scrollKey];
      } else {
        scrollState.scalar = 0;
      }
    });
  };

  return useLocalObservable(() => ({
    // Stack-based company navigation for overlay mode
    // Each entry: { company, savedTab, savedComparablesId } - savedTab/savedComparablesId restore on back nav
    companiesStack: [],
    isBackNavigation: false,

    get detailsDisplayed() {
      const top = this.companiesStack[this.companiesStack.length - 1];
      return top?.company || null;
    },

    get savedTabForCurrentCompany() {
      const top = this.companiesStack[this.companiesStack.length - 1];
      return top?.savedTab || null;
    },

    get savedComparablesIdForCurrentCompany() {
      const top = this.companiesStack[this.companiesStack.length - 1];
      return top?.savedComparablesId || null;
    },

    /**
     * Push company to stack, close overlay, or perform unmount cleanup
     *
     * @param {Object|null} entry - Company to display, or null to close/cleanup
     * @param {string|boolean|null} currentTabOrUnmount - Current tab name to save, OR true for unmount cleanup
     * @param {string|null} currentComparablesId - Current comparables ID to save
     *
     * Behavior:
     * - displayDetails(company, tab, comparablesId): Push company, save tab in prev entry
     * - displayDetails(null): Clear stack, scroll to saved position, delete scroll entry
     * - displayDetails(null, true): Just reset scroll state (unmount cleanup, no scroll)
     */
    displayDetails(entry, currentTabOrUnmount = null, currentComparablesId = null) {
      // Legacy unmount cleanup: displayDetails(null, true)
      if (currentTabOrUnmount === true) {
        clearScrollState();
        return;
      }

      const currentTab = typeof currentTabOrUnmount === 'string' ? currentTabOrUnmount : null;

      if (entry) {
        if (this.companiesStack.length === 0) {
          // First company - save scroll position
          if (scrollKey) {
            scrollState.keyed[scrollKey] = window.scrollY;
          } else {
            scrollState.scalar = window.scrollY;
          }
        } else {
          // Save current tab and comparablesId in PREVIOUS entry (so it's restored on back)
          const prevEntry = this.companiesStack[this.companiesStack.length - 1];
          if (prevEntry) {
            if (currentTab) prevEntry.savedTab = currentTab;
            if (currentComparablesId) prevEntry.savedComparablesId = currentComparablesId;
          }
        }
        // Push new company (no saved state yet - it will be set when navigating away)
        this.companiesStack.push({ company: entry, savedTab: null, savedComparablesId: null });
      } else {
        // Clear entire stack (close overlay)
        this.companiesStack.length = 0;
        setTimeout(restoreScrollAndClear, 20);
      }
    },

    goBackToPreviousCompany() {
      if (this.companiesStack.length > 1) {
        this.companiesStack.pop();
        this.isBackNavigation = true; // Signal to restore tab, skip reset
      } else {
        // Last company - close overlay and restore scroll
        this.companiesStack.length = 0;
        setTimeout(restoreScrollAndClear, 20);
      }
    },

    clearBackNavigationFlag() {
      this.isBackNavigation = false;
    }
  }));
};
